// Timezone-agnostic date helpers for strict date-only handling
// Use local calendar values, avoid toISOString (UTC) to prevent day shifts

const pad = (n) => String(n).padStart(2, '0');

export const isISODateString = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

/** Zona del negocio. Es la misma que el backend declara en TIME_ZONE. */
export const ZONA_ARGENTINA = 'America/Argentina/Buenos_Aires';

/**
 * Fecha de hoy en Argentina, como `YYYY-MM-DD`.
 *
 * **Usar esta y no `new Date().toISOString().slice(0, 10)`.** `toISOString()`
 * devuelve la fecha en UTC: con Argentina en UTC−3, todo lo que pasa entre las
 * 21:00 y la medianoche cae al día siguiente. Eso hacía que un parte cargado a
 * las 22:00 se propusiera con la fecha de mañana y el backend lo rechazara por
 * fecha futura, y que los formularios de la jornada tarde arrancaran con el día
 * equivocado.
 *
 * Se resuelve contra la zona horaria de Argentina y no contra la del
 * dispositivo, por dos razones: el backend guarda y valida en esa zona
 * (`timezone.localdate()`), así que es la única que hace coincidir las dos
 * puntas; y un celular con la zona mal configurada —cosa común en equipos de
 * obra que se resetean— no arrastra el error a los datos.
 *
 * Se usa el identificador IANA en vez de un −3 fijo: hoy son equivalentes
 * porque Argentina no aplica horario de verano desde 2009, pero si algún día
 * vuelve, esto sigue dando la fecha correcta sin tocar nada.
 */
export const hoyArgentina = () => {
  try {
    const partes = new Intl.DateTimeFormat('en-CA', {
      timeZone: ZONA_ARGENTINA,
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date());

    const valor = (tipo) => partes.find((p) => p.type === tipo)?.value;
    const y = valor('year');
    const m = valor('month');
    const d = valor('day');
    if (y && m && d) return `${y}-${m}-${d}`;
  } catch {
    // Cae al cálculo manual de abajo.
  }

  // Respaldo para entornos sin soporte de zonas horarias en Intl: se corre el
  // reloj UTC tres horas hacia atrás, que es el desfase vigente de Argentina.
  const enArgentina = new Date(Date.now() - 3 * 60 * 60 * 1000);
  return `${enArgentina.getUTCFullYear()}-${pad(enArgentina.getUTCMonth() + 1)}-${pad(enArgentina.getUTCDate())}`;
};

export const toLocalYMD = (date = new Date()) => {
  // Build YYYY-MM-DD using local time components
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  return `${y}-${m}-${d}`;
};

// Detects ISO datetime with timezone (Z or ±HH:MM)
const isISODateTimeWithTZ = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:?\d{2})$/.test(s);

export const parseDateLocal = (s) => {
  if (!s) return null;
  if (isISODateString(s)) {
    const [y, m, d] = s.split('-').map(Number);
    // Local midnight without timezone effects
    return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
  }
  // If it's an ISO datetime with timezone, use UTC components to derive the calendar date
  if (isISODateTimeWithTZ(s)) {
    const d = new Date(s);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0);
  }
  try {
    const d = new Date(s);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  } catch {
    return null;
  }
};

export const formatDateDisplay = (v) => {
  if (!v) return '-';
  if (isISODateString(v)) {
    const [y, m, d] = v.split('-');
    return `${d}-${m}-${y}`;
  }
  if (isISODateTimeWithTZ(v)) {
    const d = new Date(v);
    // Use UTC parts to avoid timezone-induced day shifts
    const day = pad(d.getUTCDate());
    const mon = pad(d.getUTCMonth() + 1);
    const yr = d.getUTCFullYear();
    return `${day}-${mon}-${yr}`;
  }
  try {
    const d = new Date(v);
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
  } catch {
    return String(v);
  }
};

const dateUtils = { isISODateString, toLocalYMD, hoyArgentina, parseDateLocal, formatDateDisplay };
export default dateUtils;
